import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SurveyService } from '../../services/surveys.service';
import { ToastService } from '../../services/general/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { faFileExcel, faUpload, faArrowRight, faCheckCircle, faExclamationTriangle, faChevronLeft, faChevronRight, faFileImport, faTimes, faUser, faEnvelope, faPhone, faSitemap, faCog, faLayerGroup, faIdCard, faDownload } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-modal-import-survey-team',
  templateUrl: './modal-import-survey-team.component.html',
  styleUrls: ['./modal-import-survey-team.component.scss'],
  standalone: false
})
export class ModalImportSurveyTeamComponent implements OnInit {

  readonly faFileExcel = faFileExcel;
  readonly faUpload = faUpload;
  readonly faArrowRight = faArrowRight;
  readonly faCheckCircle = faCheckCircle;
  readonly faExclamationTriangle = faExclamationTriangle;
  readonly faChevronLeft = faChevronLeft;
  readonly faChevronRight = faChevronRight;
  readonly faFileImport = faFileImport;
  readonly faTimes = faTimes;
  readonly faUser = faUser;
  readonly faEnvelope = faEnvelope;
  readonly faPhone = faPhone;
  readonly faSitemap = faSitemap;
  readonly faCog = faCog;
  readonly faLayerGroup = faLayerGroup;
  readonly faIdCard = faIdCard;
  readonly faDownload = faDownload;

  step = 1;
  loading = false;
  fileName = '';
  selectedFile: File | null = null;

  excelColumns: string[] = [];
  samples: any[] = [];
  totalRows = 0;

  // Mapping state
  mapping: any = {
    name: '',
    last_name: '',
    email: '',
    phone_number: '',
    role: ''
  };
  customFieldsMapping: any = {}; // id -> excelColumn index

  startRow = 2; // Default skip header
  endRow: number | null = null;
  defaultRole = '';
  skippedEmails: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<ModalImportSurveyTeamComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string, teamMemberCustomFields: any[] },
    private surveyService: SurveyService,
    private toast: ToastService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    // Initialize custom fields mapping
    this.data.teamMemberCustomFields.forEach(cf => {
      this.customFieldsMapping[cf.id] = '';
    });
  }

  onFileChange(evt: any) {
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) return;

    this.selectedFile = target.files[0];
    this.fileName = this.selectedFile.name;
    this.loading = true;

    this.surveyService.importSurveyTeamPreview(this.data.surveyId || 'new', this.selectedFile, (res) => {
      this.excelColumns = res.headers.map((h: any, i: number) => h ? h.toString() : `Col ${i + 1}`);
      this.samples = res.samples;
      this.totalRows = res.total_rows;
      this.endRow = this.totalRows + 1; // Backend total_rows is count - 1 (header)

      this.autoMapHeaders(this.excelColumns);
      this.step = 2;
      this.loading = false;
    }, (err) => {
      this.loading = false;
      this.toast.error(this.translateService.instant('modals.import-survey-team.error-preview'));
    });
  }

  autoMapHeaders(headers: string[]) {
    const findColumn = (keywords: string[]) => {
      return headers.findIndex(h => keywords.some(k => h.toLowerCase().includes(k.toLowerCase())));
    };

    const nameIdx = findColumn(['nome', 'first name', 'name']);
    if (nameIdx !== -1) this.mapping.name = nameIdx.toString();

    const lastNameIdx = findColumn(['cognome', 'last name', 'surname']);
    if (lastNameIdx !== -1) this.mapping.last_name = lastNameIdx.toString();

    const emailIdx = findColumn(['email', 'mail', 'posta']);
    if (emailIdx !== -1) this.mapping.email = emailIdx.toString();

    const phoneIdx = findColumn(['telefono', 'phone', 'cellulare', 'mobile']);
    if (phoneIdx !== -1) this.mapping.phone_number = phoneIdx.toString();

    const roleIdx = findColumn(['ruolo', 'role', 'posizione']);
    if (roleIdx !== -1) this.mapping.role = roleIdx.toString();

    // Auto-map custom fields
    this.data.teamMemberCustomFields.forEach(cf => {
      const cfIdx = findColumn([cf.name]);
      if (cfIdx !== -1) this.customFieldsMapping[cf.id] = cfIdx.toString();
    });
  }

  goToStep(s: number) {
    if (s === 3) {
      if (this.mapping.email === '' || this.mapping.email === null) {
        this.toast.error(this.translateService.instant('modals.import-survey-team.email-required'));
        return;
      }
      if (this.mapping.name === '' || this.mapping.name === null) {
        this.toast.error(this.translateService.instant('modals.import-survey-team.name-required'));
        return;
      }
    }
    this.step = s;
  }

  get importedMembersCount(): number {
    if (!this.totalRows) return 0;
    // totalRows from backend is (total_rows - 1), so total rows in excel is totalRows + 1
    const totalExcelRows = this.totalRows + 1;
    const end = this.endRow || totalExcelRows;
    const start = this.startRow || 1;

    return Math.max(0, Math.min(totalExcelRows, end) - Math.max(1, start) + 1);
  }

  get previewSamples(): any[] {
    if (!this.samples || this.samples.length === 0) return [];

    // Backend 'samples' are rows from index 1 to 10 (Excel rows 2 to 11).
    // So samples[0] is Excel row 2.
    // We want to show samples that fall within [startRow, endRow].

    const start = this.startRow || 2;
    const end = this.endRow || (this.totalRows + 1);

    return this.samples.filter((_, index) => {
      const excelRow = index + 2;
      return excelRow >= start && excelRow <= end;
    });
  }

  removeRow(row: any) {
    const emailCol = this.mapping.email;
    const email = row[emailCol];
    if (email) {
      this.skippedEmails.push(email);
    }
    const index = this.samples.indexOf(row);
    if (index !== -1) {
      this.samples.splice(index, 1);
      this.totalRows--;
    }
  }

  confirmImport() {
    if (!this.selectedFile) return;

    this.loading = true;
    const finalMapping = {
      ...this.mapping,
      customs: this.customFieldsMapping,
      skipped_emails: this.skippedEmails
    };

    this.surveyService.bulkAddSurveyTeamMembersImport(
      this.data.surveyId || 'new',
      this.selectedFile,
      finalMapping,
      this.startRow,
      this.endRow,
      this.defaultRole,
      (res) => {
        this.loading = false;
        if (res.count > 0) {
          this.toast.success(this.translateService.instant('modals.import-survey-team.import-success'), this.translateService.instant('modals.import-survey-team.import-success-desc', { count: res.count }));

          if (!this.data.surveyId && res.data) {
            this.dialogRef.close({ imported: res.data });
          } else {
            this.dialogRef.close(true);
          }
        } else {
          this.toast.warn(this.translateService.instant('modals.import-survey-team.no-members-to-import'), this.translateService.instant('modals.import-survey-team.no-members-desc'));
        }
      }, (err) => {
        this.loading = false;
        this.toast.error(this.translateService.instant('modals.import-survey-team.import-failed'));
      }
    );
  }

  close() {
    this.dialogRef.close();
  }

  objectKeys(obj: any): string[] {
    return obj ? Object.keys(obj) : [];
  }

  downloadTemplate(event: MouseEvent) {
    event.stopPropagation();
    const headers = [
      this.translateService.instant('modals.import-survey-team.template.name'),
      this.translateService.instant('modals.import-survey-team.template.last-name'),
      this.translateService.instant('modals.import-survey-team.template.email'),
      this.translateService.instant('modals.import-survey-team.template.phone'),
      this.translateService.instant('modals.import-survey-team.template.role')
    ];

    this.data.teamMemberCustomFields.forEach(cf => {
      headers.push(cf.name);
    });

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers]);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    XLSX.writeFile(wb, this.translateService.instant('modals.import-survey-team.template.filename'));
  }
}
